import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { CheckCircle, ClipboardList } from 'lucide-react'
import { Button } from '@diversioteam/diversio-ds'

import * as S from './GlobalOnboardingBanner.styles'

import { useOnboardingStatus } from '@/hooks/onboarding/useOnboardingStatus'
import type { RootState } from '@/store/store'
import { UserRole } from '@/types/auth.types'

type Props = {
    className?: string
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000

export function GlobalOnboardingBanner({ className }: Props) {
    const { organization, user } = useSelector((s: RootState) => s.auth)
    const location = useLocation()
    const navigate = useNavigate()
    const orgUuid = organization?.uuid
    const role = user?.role ? (typeof user.role === 'object' ? user.role.role : user.role) : null
    const isOwner = role === UserRole.OWNER
    const isHR =
        role && [UserRole.HR_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.OWNER].includes(role as UserRole)
    const isManager = role === UserRole.PEOPLE_MANAGER

    // Owners, HR, and Managers see this ribbon; unified org status comes from the unified endpoint
    const shouldShowBanner = Boolean((isOwner || isHR || isManager) && orgUuid)
    const { data } = useOnboardingStatus(shouldShowBanner)
    const onboardingStatus = data?.organization
    const employeeStatus = data?.employee

    const snoozeKey = useMemo(() => (orgUuid ? `onboard:banner_snooze:${orgUuid}` : undefined), [orgUuid])
    const welcomeSeenKey = useMemo(() => (orgUuid ? `onboard:welcome_seen:${orgUuid}` : undefined), [orgUuid])
    const [snoozed, setSnoozed] = useState<boolean>(false)
    const [welcomeSeen, setWelcomeSeen] = useState<boolean>(false)

    useEffect(() => {
        if (!snoozeKey) return
        const raw = localStorage.getItem(snoozeKey)
        if (!raw) return
        const until = Number(raw)
        if (Number.isFinite(until) && Date.now() < until) setSnoozed(true)
    }, [snoozeKey])

    useEffect(() => {
        if (!welcomeSeenKey) return
        const seen = localStorage.getItem(welcomeSeenKey)
        setWelcomeSeen(seen === '1')
    }, [welcomeSeenKey])

    // Hide banner on Dashboard page to avoid redundancy
    const isDashboard = location.pathname.startsWith('/dashboard')

    // Show banner for different roles based on their status
    const showForOwnerOrHR = (isOwner || isHR) && onboardingStatus
    const showForManager = isManager && employeeStatus && !employeeStatus.has_completed_intake

    if (!orgUuid || (!showForOwnerOrHR && !showForManager)) return null
    if (isDashboard) return null

    // Show the welcome banner only when all tasks are complete,
    // even if a previous session handoff flag exists.
    const allCompleted = onboardingStatus?.completed_tasks === onboardingStatus?.total_tasks
    const sessionWelcome = sessionStorage.getItem(`onboard:banner:${orgUuid}`) === 'org'
    const showWelcome = onboardingStatus && allCompleted && (!welcomeSeen || sessionWelcome)

    if (showWelcome) {
        return (
            <S.BannerContainer $variant="success" className={className}>
                <S.BannerContent>
                    <S.IconWrapper $variant="success">
                        <CheckCircle size={20} strokeWidth={2} />
                    </S.IconWrapper>
                    <S.BannerText>
                        <S.BannerTitle>Welcome to Optimo!</S.BannerTitle>
                        <S.BannerDescription>
                            Your organization setup is complete. Explore your dashboard for next steps.
                        </S.BannerDescription>
                    </S.BannerText>
                </S.BannerContent>
                <S.BannerActions>
                    <Button color="primary" onClick={() => navigate('/dashboard')} style={{ whiteSpace: 'nowrap' }}>
                        Explore Dashboard
                    </Button>
                    <Button
                        color="secondaryLight"
                        onClick={() => {
                            if (welcomeSeenKey) localStorage.setItem(welcomeSeenKey, '1')
                            sessionStorage.removeItem(`onboard:banner:${orgUuid}`)
                            setWelcomeSeen(true)
                        }}
                    >
                        Dismiss
                    </Button>
                </S.BannerActions>
            </S.BannerContainer>
        )
    }

    // Show manager intake incomplete banner
    if (isManager && employeeStatus && !employeeStatus.has_completed_intake && !snoozed) {
        return (
            <S.BannerContainer $variant="info" className={className}>
                <S.BannerContent>
                    <S.IconWrapper $variant="info">
                        <ClipboardList size={20} strokeWidth={2} />
                    </S.IconWrapper>
                    <S.BannerText>
                        <S.BannerTitle>Complete your intake survey</S.BannerTitle>
                        <S.BannerDescription>Please complete the intake survey to get started</S.BannerDescription>
                    </S.BannerText>
                </S.BannerContent>
                <S.BannerActions>
                    <Button color="primary" onClick={() => navigate('/intake')} style={{ whiteSpace: 'nowrap' }}>
                        Take Survey
                    </Button>
                    <Button
                        color="secondaryLight"
                        onClick={() => {
                            if (snoozeKey) localStorage.setItem(snoozeKey, String(Date.now() + ONE_DAY_MS))
                            setSnoozed(true)
                        }}
                    >
                        Not now
                    </Button>
                </S.BannerActions>
            </S.BannerContainer>
        )
    }

    // If not onboarded yet, and not snoozed, show the resume banner
    if (!onboardingStatus || allCompleted || snoozed) return null

    const remaining = Math.max(0, (onboardingStatus?.total_tasks || 0) - (onboardingStatus?.completed_tasks || 0))
    const first = onboardingStatus?.first_incomplete_task
    const ctaUrl = first?.action?.url || '/dashboard'

    return (
        <S.BannerContainer $variant="info" className={className}>
            <S.BannerContent>
                <S.IconWrapper $variant="info">
                    <ClipboardList size={20} strokeWidth={2} />
                </S.IconWrapper>
                <S.BannerText>
                    <S.BannerTitle>Finish setting up your organization</S.BannerTitle>
                    <S.BannerDescription>
                        {remaining === 1 ? '1 task left' : `${remaining} tasks remaining`}
                    </S.BannerDescription>
                </S.BannerText>
            </S.BannerContent>
            <S.BannerActions>
                <Button color="primary" onClick={() => navigate(ctaUrl)} style={{ whiteSpace: 'nowrap' }}>
                    Resume Setup
                </Button>
                <Button
                    color="secondaryLight"
                    onClick={() => {
                        if (snoozeKey) localStorage.setItem(snoozeKey, String(Date.now() + ONE_DAY_MS))
                        setSnoozed(true)
                    }}
                >
                    Not now
                </Button>
            </S.BannerActions>
        </S.BannerContainer>
    )
}
